import type { Permission } from '@teachino/shared-util';
import { useGlobalDependencyProvider } from '@teachino/shared-util';
import { computed, type ComputedRef } from 'vue';
import { shouldShowProFeatureBadge } from './pro-feature-badge-visibility';
import { canUseProFeature } from './soft-paywall';
import { useLicense } from './useLicense';

const ADD_GRADES: Permission = 'add_grades';
const ADD_NOTES: Permission = 'add_notes';
const ADD_NOTES_SEE: Permission = 'add_notes_see';

/**
 * Grading ("Beurteilungen") and documentation notes ("Notizen") access in one place.
 *
 * Both keys go through {@link canUseProFeature}, so the paid / legacy-grace / trial-expiry
 * rules stay identical to every other Pro feature — there is deliberately no second
 * decision function here. `GradingFeaturePermissionService` in apps/core mirrors this
 * module branch for branch; the two must be changed together.
 */
function useGradingFeatures() {
  const user = useGlobalDependencyProvider().inject('user');
  const { paywallLicenses, hasLoadedLicenses } = useLicense();

  function canUse(feature: Permission, hasFeaturePermission: boolean): boolean {
    // While the Licenses query is in flight the license list is empty, which
    // would read as "no trial, no paid plan" and deny a paying user. Fall back
    // to the raw feature flag from `me` — it is served synchronously and the
    // backend enforces the same rules anyway.
    if (!hasLoadedLicenses.value) {
      return hasFeaturePermission;
    }

    return canUseProFeature(
      feature,
      hasFeaturePermission,
      paywallLicenses.value,
      user.accountCreatedAt.value,
    );
  }

  const canAddGrades = computed(() =>
    canUse(ADD_GRADES, user.hasPermission(ADD_GRADES).value),
  );

  const canAddNotes = computed(() =>
    canUse(ADD_NOTES, user.hasPermission(ADD_NOTES).value),
  );

  // The see/use split of the integration flags: `add_notes_see` shows the note
  // UI, `add_notes` allows writing. Visible but not usable means crown and
  // upgrade prompt (the free tiers); neither means no note UI at all
  // (Luxembourg, South Tyrol). Usage implies visibility, so a paid plan can
  // never lose the UI to a missing tick.
  const canSeeNotes = computed(
    () => user.hasPermission(ADD_NOTES_SEE).value || canAddNotes.value,
  );

  return { paywallLicenses, canAddGrades, canAddNotes, canSeeNotes };
}

/** Whether the user may create or edit grades (grace, trial, and license rules applied). */
export function useCanAddGrades(): ComputedRef<boolean> {
  return useGradingFeatures().canAddGrades;
}

/** Whether the user may create or edit documentation notes. */
export function useCanAddNotes(): ComputedRef<boolean> {
  return useGradingFeatures().canAddNotes;
}

/** Whether note-shaped UI is shown at all; with the upgrade prompt when notes cannot be written. */
export function useCanSeeNotes(): ComputedRef<boolean> {
  return useGradingFeatures().canSeeNotes;
}

/** Pro badge visibility for grade-adding affordances. */
export function useShowAddGradesBadge(): ComputedRef<boolean> {
  const { paywallLicenses, canAddGrades } = useGradingFeatures();

  return computed(() =>
    shouldShowProFeatureBadge(canAddGrades.value, paywallLicenses.value),
  );
}

/** Pro badge visibility for note-adding affordances. */
export function useShowAddNotesBadge(): ComputedRef<boolean> {
  const { paywallLicenses, canAddNotes } = useGradingFeatures();

  return computed(() =>
    shouldShowProFeatureBadge(canAddNotes.value, paywallLicenses.value),
  );
}
